import { ref, readonly } from 'vue'

import useAxiosResponse from '@/composables/common/useAxiosResponse'
import { setLoginLocaleCode } from '@/composables/localization/useLocalization'
import userArmService from '@/services/api/user'
import { type UserProfile } from '@/types/Auth'
import { type CustomAxiosConfig } from '@/types/axios'
import { isTokenExpired } from '@/utils/token'

export const loggedIn = ref(false)
const accessToken = ref<string | null>(null)
const refreshToken = ref<string | null>(null)
const userProfile = ref<UserProfile | null>(null)

const userProfileQuery = useAxiosResponse(async () => {
  return await userArmService.fetchProfile({ requestErrorEnabled: true })
}, {
  onSuccess (data) {
    userProfile.value = data
    setLoginLocaleCode(userProfile.value.language_preference)
  },
  loadingMode: 'normal'
})

export const getUserProfile = (): UserProfile | null => {
  return userProfile.value
}

export const setUserProfile = (value: UserProfile): void => {
  userProfile.value = value
  setLoginLocaleCode(userProfile.value.language_preference)
}

const actions = {
  async login (data: { email: string, password: string }, config?: CustomAxiosConfig) {
    return await userArmService.login(data, config)
  },
  async logout () {
    await new Promise<void>((resolve) => {
      actions.clearData()
      resolve()
    })
  },
  setAccessToken (token: string) {
    localStorage.setItem('access_token', token)
  },
  setRefreshToken (token: string) {
    localStorage.setItem('refresh_token', token)
  },
  validateToken (): boolean {
    loggedIn.value = false
    accessToken.value = localStorage.getItem('access_token')
    if (accessToken.value !== null && isTokenExpired(accessToken.value)) {
      actions.clearData()
    } else if (accessToken.value !== null) {
      loggedIn.value = true
      refreshToken.value = localStorage.getItem('refresh_token')
    }

    return loggedIn.value
  },
  getAccessToken (): string | null {
    return localStorage.getItem('access_token')
  },
  clearData () {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    loggedIn.value = false
    userProfile.value = null
    accessToken.value = null
    refreshToken.value = null
  }
}

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default () => {
  return readonly({
    loggedIn,
    accessToken,
    refreshToken,
    userProfile,
    userProfileQuery,
    ...actions,
    setUserProfile
  })
}
